import { IBannerInlinePlaceholderProps } from '@tekface/smart-activation';
import {
  BlockItemType,
  BlockType,
  DiscoveryBlock,
  PageTypeEnum,
} from '@teko/discovery';
import { SearchProductFilter } from '@teko/discovery/v2';
import { Container, styled } from '@teko/ds-desktop';
import { BannerEntity } from '@teko/ecommerce';
import { BannerProps } from '@teko/teko-ecommerce';
import { FilterInterface, ProductCardProps } from '@teko/types/ecommerce';
import { cleanObject } from '@teko/utils';
import { InlineBanner } from 'components/InlineBanner';
import { findBlockByType, getBlockDomId } from 'helpers/block';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { Box, Flex } from 'reflexbox';
import { ObjectMap } from 'types';
import { INLINE_BANNER_CLASSNAME } from '../../../constants';
import { LazyComponent } from '../../LazyComponent';
import { RecentViewedProducts } from './RecentViewedProducts';

const DiscoveryRecommendedProducts = dynamic(() =>
  import('../../DiscoveryRecommendedProducts').then(
    (mod) => mod.DiscoveryRecommendedProducts,
  ),
);

const ProductListWithFilter = dynamic(() =>
  import('modules/search-page/desktop').then(
    (mod) => mod.ProductListWithFilter,
  ),
);

const TIME_TO_RENDER = 0;

const FeaturedBanners = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBanners),
);

const PromoteBlock = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.PromoteBlock),
);

const HotDealSection = dynamic(() =>
  import('./HotDealSection').then((mod) => mod.HotDealSection),
);

const BestSellingProductsBlock = dynamic(() =>
  import('./BestSellingProductsBlock').then(
    (mod) => mod.BestSellingProductsBlock,
  ),
);

const NavigationBarBlock = dynamic(() =>
  import('./NavigationBarBlock').then((mod) => mod.NavigationBarBlock),
);

const YoutubeBlock = dynamic(() =>
  import('./YoutubeBlock').then((mod) => mod.YoutubeBlock),
);

const LeftRightStickyBannerBlock = dynamic(() =>
  import('./LeftRightStickyBannerBlock').then(
    (mod) => mod.LeftRightStickyBannerBlock,
  ),
);

const ListCategories = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ListCategories),
);

const FeaturedBrands = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.FeaturedBrands),
);

const FlashSaleBlock = dynamic(() =>
  import('./FlashSaleBlock').then((mod) => mod.FlashSaleBlock),
);

const BlockAnchor = styled.div`
  position: relative;
  top: -92px;
`;

interface DiscoveryBlocksProps {
  blocks: (DiscoveryBlock & { ssr?: boolean })[];
  customRenderer?: (block: DiscoveryBlock, index: number) => React.ReactNode;
  ssrMapProducts?: { [blockId: string]: ProductCardProps[] }; // for ssr
  cookedFilters?: FilterInterface[];
  filterPrice?: { min: number; max: number };
  filters?: SearchProductFilter;
  inlineBanners?: IBannerInlinePlaceholderProps<{}>['banners'];
  pageTypeEnum?: PageTypeEnum;
  isHeadingBlockTitle?: boolean;
}
export const DiscoveryBlocks: React.FC<DiscoveryBlocksProps> = ({
  blocks,
  customRenderer,
  filterPrice = undefined,
  ssrMapProducts,
  filters,
  inlineBanners,
  pageTypeEnum,
  isHeadingBlockTitle = false,
}: DiscoveryBlocksProps) => {
  const categoryProductBlockRendered: string[] = [];
  const router = useRouter();
  const { query } = router;
  const mainSkuList = findBlockByType(BlockType.MAIN_SKU_LIST, blocks);
  const leftStickyBanner = findBlockByType(
    BlockType.LEFT_STICKY_BANNER,
    blocks,
  );
  const rightStickyBanner = findBlockByType(
    BlockType.RIGHT_STICKY_BANNER,
    blocks,
  );
  const youtubeBlock = findBlockByType(BlockType.YOUTUBE, blocks);
  const navigationBarBlock = findBlockByType(BlockType.NAVIGATION_BAR, blocks);
  const isDefaultBlock = blocks.length === 2 && mainSkuList;

  if (isDefaultBlock) {
    const fetchParams = {
      ...mainSkuList.content.fetchParams,
      blockId: mainSkuList.id,
    };
    const filterParams = {
      ...fetchParams.filter,
      ...fetchParams.sorting,
      blockId: mainSkuList.id,
    } as ObjectMap;
    return (
      <ProductListWithFilter
        serverProducts={ssrMapProducts?.[mainSkuList.id]}
        filterPrice={filterPrice}
        serverFilters={filters}
        customerParams={cleanObject({ ...filterParams, ...query })}
      />
    );
  }

  const firstCountdownBlockIndex = blocks.findIndex(
    (block) => block.type === BlockType.COUNT_DOWN_PROMO_BIG_SKU,
  );

  return (
    <div>
      {navigationBarBlock && (
        <NavigationBarBlock block={navigationBarBlock} blockList={blocks} />
      )}
      {youtubeBlock && (
        <React.Fragment>
          <BlockAnchor id={getBlockDomId(youtubeBlock)} />
          <YoutubeBlock block={youtubeBlock} />
        </React.Fragment>
      )}
      {(leftStickyBanner || rightStickyBanner) && (
        <LeftRightStickyBannerBlock
          leftBlock={leftStickyBanner}
          rightBlock={rightStickyBanner}
          stickyTop={navigationBarBlock ? 112 : 72}
        />
      )}
      {firstCountdownBlockIndex === -1 && pageTypeEnum === PageTypeEnum.HOME ? (
        <Container>
          <InlineBanner
            inlineBanners={inlineBanners}
            inlineBannerClass={INLINE_BANNER_CLASSNAME.INLINE_HOME_BODY}
          />
        </Container>
      ) : null}
      {blocks.map((block, index, list) => {
        const showBannerHorizontal =
          block.itemType === BlockItemType.BANNER &&
          block.type.startsWith('HORIZONTAL') &&
          block.type !== BlockType.HORIZONTAL_4_BANNER &&
          block?.cookedData?.length;
        const showCategoryProductBlock =
          block.type === BlockType.MIX_BANNER &&
          index > 1 &&
          !categoryProductBlockRendered.includes(list[index - 1].id) &&
          list[index - 1].type === BlockType.MIX_BANNER;
        const showTopBrands =
          block.itemType === BlockItemType.MENU &&
          block.type === BlockType.HORIZONTAL_HIGHLIGHTED_BANNER &&
          block?.cookedData?.length;
        const showCustomCategories =
          block.type === BlockType.PROMOTE_CATEGORY_MENU &&
          block?.cookedData?.length;

        const ConditionalLazyComponent = block.ssr
          ? React.Fragment
          : LazyComponent;

        if (showCustomCategories) {
          return (
            <Container marginBottom="s24" key={block.id}>
              <BlockAnchor id={getBlockDomId(block)} />
              <ListCategories
                regionName="categoryList"
                categories={block.cookedData.map((category: BannerEntity) => {
                  return {
                    id: category.id,
                    title: category.campaignName,
                    image: { src: category.image },
                    link: { href: category.targetPath },
                  };
                })}
                header={{ title: block.label }}
                isHeadingTitle={isHeadingBlockTitle}
              />
            </Container>
          );
        }

        if (showTopBrands)
          return (
            <ConditionalLazyComponent
              key={block.id}
              timeToRender={TIME_TO_RENDER}
            >
              <Container marginBottom="s24">
                <BlockAnchor id={getBlockDomId(block)} />
                <FeaturedBrands
                  header={{
                    title: block.label,
                  }}
                  brands={block.cookedData.map((bl: BannerEntity) => ({
                    name: bl.campaignName,
                    description: bl.description,
                    image: {
                      src: bl.image,
                    },
                    link: {
                      href: bl.targetPath,
                    },
                  }))}
                  isHeadingTitle={isHeadingBlockTitle}
                />
              </Container>
            </ConditionalLazyComponent>
          );

        if (showCategoryProductBlock) {
          categoryProductBlockRendered.push(block.id);
          categoryProductBlockRendered.push(list[index - 1].id);
          const categoryBlock1 = list[index - 1].cookedData;
          const categoryBlock2 = block.cookedData;
          return (
            <ConditionalLazyComponent
              key={block.id}
              timeToRender={TIME_TO_RENDER}
            >
              <Container>
                <BlockAnchor id={getBlockDomId(block)} />
                <Flex marginBottom={24} flexWrap="wrap" mx={-2}>
                  <Box width={[1, 1 / 2]} px={2}>
                    <PromoteBlock {...categoryBlock1} />
                  </Box>
                  <Box width={[1, 1 / 2]} px={2}>
                    <PromoteBlock {...categoryBlock2} />
                  </Box>
                </Flex>
              </Container>
            </ConditionalLazyComponent>
          );
        }

        // block.cookedData is the result of transformBanner function
        if (showBannerHorizontal) {
          const banners = (block.cookedData as BannerEntity[]).map(
            (banner): BannerProps => {
              return {
                src: banner.image,
                link: {
                  href: banner.targetPath,
                },
                name: banner.campaignName,
              };
            },
          );

          return (
            <Container marginBottom="s24" key={block.id}>
              <BlockAnchor id={getBlockDomId(block)} />
              <FeaturedBanners
                banners={banners}
                regionName={`horizontalBanner${banners.length}`}
              />
            </Container>
          );
        }

        if (customRenderer) {
          // for other block types
          // allow an input customRenderer to overwrite
          // the default input
          const blockNode = customRenderer(block, index);
          if (blockNode) return blockNode;
        }

        if (
          block.type === BlockType.COUNT_DOWN_PROMO_SKU ||
          block.type === BlockType.FLASH_SALE
        ) {
          return (
            <React.Fragment key={block.id}>
              <BlockAnchor id={getBlockDomId(block)} />
              <FlashSaleBlock
                block={block}
                ssrProducts={ssrMapProducts?.[block.id]}
                isHeadingTitle={isHeadingBlockTitle}
              />
            </React.Fragment>
          );
        }

        if (
          block.type === BlockType.HORIZONTAL_SKU_LIST ||
          block.type === BlockType.BEST_SELLING_PRODUCT
        ) {
          return (
            <ConditionalLazyComponent
              key={block.id}
              timeToRender={TIME_TO_RENDER}
            >
              <Container marginBottom="s24">
                <BlockAnchor id={getBlockDomId(block)} />
                <BestSellingProductsBlock
                  block={block}
                  ssrProducts={ssrMapProducts?.[block.id]}
                  isHeadingTitle={isHeadingBlockTitle}
                />
              </Container>
            </ConditionalLazyComponent>
          );
        }

        if (block.type === BlockType.COUNT_DOWN_PROMO_BIG_SKU) {
          const serverItemId = block?.cookedData?.[0]?.paramsObject?.itemId;
          const ssrProducts = ssrMapProducts?.[`${block.id}_${serverItemId}`];
          return (
            <React.Fragment key={block.id}>
              <Container marginBottom="s24">
                <BlockAnchor id={getBlockDomId(block)} />
                <HotDealSection
                  block={block}
                  ssrProducts={ssrProducts}
                  isHeadingTitle={isHeadingBlockTitle}
                />
              </Container>
              {firstCountdownBlockIndex === index &&
              pageTypeEnum === PageTypeEnum.HOME ? (
                <Container
                  style={{ overflow: 'hidden' }}
                  marginBottom="s24"
                  key="BannerInlinePlaceholder"
                >
                  <InlineBanner
                    inlineBanners={inlineBanners}
                    inlineBannerClass={INLINE_BANNER_CLASSNAME.INLINE_HOME_BODY}
                  />
                </Container>
              ) : null}
            </React.Fragment>
          );
        }
        if (
          block.type === BlockType.MAIN_SKU_LIST ||
          block.type === BlockType.PROMOTE_SELLER
        ) {
          return (
            <ConditionalLazyComponent
              key={block.id}
              timeToRender={TIME_TO_RENDER}
            >
              <Container marginBottom="s24" key={block.id}>
                <BlockAnchor id={getBlockDomId(block)} />
                <DiscoveryRecommendedProducts
                  title={block.label}
                  paramsObject={{
                    ...block.cookedData.paramsObject,
                    blockId: block.id,
                  }}
                  actionLink={
                    block.viewMoreUrl ? { href: block.viewMoreUrl } : undefined
                  }
                  ssrProducts={ssrMapProducts?.[block.id]}
                  isHeadingTitle={isHeadingBlockTitle}
                />
              </Container>
            </ConditionalLazyComponent>
          );
        }

        if (block.type === BlockType.RECENT_PRODUCT) {
          return (
            <ConditionalLazyComponent key={block.id}>
              <RecentViewedProducts isHeadingTitle={isHeadingBlockTitle} />
            </ConditionalLazyComponent>
          );
        }
      })}
    </div>
  );
};
